import React, { useEffect, useRef, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../UI/DesignStandards";
import { DEFAULT_SCAN_CORNERS, FULL_PHOTO_CORNERS } from "../../../utility/checkScanWarp";

const HANDLE_SIZE = 28;
const CORNER_LABELS = ["Top left", "Top right", "Bottom right", "Bottom left"];

const imageBox = (image) => {
  if (!image) return { left: 0, top: 0, width: 0, height: 0 };
  const naturalWidth = image.naturalWidth || 1;
  const naturalHeight = image.naturalHeight || 1;
  const scale = Math.min(
    image.clientWidth / naturalWidth,
    image.clientHeight / naturalHeight
  );
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    left: (image.clientWidth - width) / 2,
    top: (image.clientHeight - height) / 2,
    width,
    height,
  };
};

const ScanCornerEditor = ({
  imageUrl,
  corners,
  applying = false,
  onChangeCorners,
  onApply,
  onUseFullPhoto,
}) => {
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const [box, setBox] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const points = corners?.length === 4 ? corners : DEFAULT_SCAN_CORNERS;

  const measure = () => {
    setBox(imageBox(imageRef.current));
  };

  useEffect(() => {
    measure();
    const node = imageRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [imageUrl]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag || !box.width || !box.height) return;
      const next = Math.min(
        1,
        Math.max(0, (event.clientX - drag.rect.left - box.left) / box.width)
      );
      const y = Math.min(
        1,
        Math.max(0, (event.clientY - drag.rect.top - box.top) / box.height)
      );
      onChangeCorners(
        points.map((point, index) =>
          index === drag.index ? { x: next, y } : point
        )
      );
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [box, onChangeCorners, points]);

  const startDrag = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const frame = event.currentTarget.parentElement?.getBoundingClientRect();
    dragRef.current = {
      index,
      rect: frame || { left: 0, top: 0 },
    };
  };

  const polygon = points
    .map((point) => {
      const x = box.left + point.x * box.width;
      const y = box.top + point.y * box.height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="space-y-3">
      <p className="text-sm text-brand-charcoal">
        Drag each corner to a corner of the page in the photo. Then apply it to
        the template.
      </p>
      <div
        className="relative w-full bg-brand-charcoal overflow-hidden touch-none"
        style={{ minHeight: 320 }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Blank check photo"
          className="block w-full max-h-[70vh] object-contain mx-auto select-none"
          draggable={false}
          onLoad={measure}
        />
        {box.width ? (
          <>
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              aria-hidden="true"
            >
              <polygon
                points={polygon}
                fill="rgba(212, 175, 55, 0.18)"
                stroke="#d4af37"
                strokeWidth="2"
              />
            </svg>
            {points.map((point, index) => (
              <button
                key={CORNER_LABELS[index]}
                type="button"
                aria-label={CORNER_LABELS[index]}
                className="absolute rounded-full bg-brand-gold border-2 border-brand-cream shadow cursor-grab active:cursor-grabbing touch-none"
                style={{
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  left: box.left + point.x * box.width - HANDLE_SIZE / 2,
                  top: box.top + point.y * box.height - HANDLE_SIZE / 2,
                }}
                onPointerDown={(event) => startDrag(event, index)}
              />
            ))}
          </>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <PrimaryButton onClick={onApply} loading={applying} disabled={applying}>
          Apply to page
        </PrimaryButton>
        <SecondaryButton
          onClick={() => onChangeCorners(DEFAULT_SCAN_CORNERS)}
          disabled={applying}
        >
          Reset corners
        </SecondaryButton>
        <SecondaryButton
          onClick={() => {
            onChangeCorners(FULL_PHOTO_CORNERS);
            if (onUseFullPhoto) onUseFullPhoto();
          }}
          disabled={applying}
        >
          Use full photo
        </SecondaryButton>
      </div>
    </div>
  );
};

export default ScanCornerEditor;
